import {
  Breadcrumb,
  Button,
  Input,
  Modal,
  Popconfirm,
  Row,
  Space,
  Table,
  Tooltip,
} from "antd";
import "antd/dist/antd.min.css";
import { debounce } from "lodash";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { getRequest, postRequest } from "../../../axios";
import ButtonField from "../../../common/htmlComponents/ButtonField";
import BulkUploadCsv from "../../../image/AddCsvFile.png";
import { ReactComponent as Archive } from "../../../image/Archive.svg";
import { ReactComponent as Archiveicon } from "../../../image/Archiveicon.svg";
import { ReactComponent as Editicon } from "../../../image/Editicon.svg";
import PlusSquareOutlined from "../../../image/PlusSquareOutlined.svg";
import SearchIcon from "../../../image/SearchIcon.svg";
import "../../../styles/Main.scss";
import { authUserData } from "../../../utils/Helper";
import { InfoCircleOutlined } from "@ant-design/icons";
import {
  ErrorNotificationMsg,
  SuccessNotificationMsg,
} from "../../../utils/Notifications";
import AddMaterialType from "./AddMaterialType";
import BulkMaterialTypeUpload from "./BulkMaterialTypeUpload";

const MaterialTypeList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { Search } = Input;
  const [selectedRow, setSelectedRow] = useState([]);
  const [reload, setReload] = useState(false);
  const [loading, setLoading] = useState(false);
  const [materialTypeList, setMaterialTypeList] = useState([]);
  const [totalCount, settotalCount] = useState(0);
  const [newMaterialTypeAdded, setNewMaterialTypeAdded] = useState(false);
  const [materialTypeId, setMaterialTypeId] = useState(null);
  const [modalKey, setModalKey] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchText, setSearchText] = useState("");

  // state initiaillisation
  const [state, setState] = useState({
    page: 1,
    ordering: "id",
    pagesize: 10,
    search: "",
    total: 0,
  });

  // state initiaillisation
  useEffect(() => {
    const getMaterialTypeList = async () => {
      setLoading(true);

      try {
                const fields = [
          "id",
          "category_tree_name",
          "type_name",
          "sub_category",
          "hsn_code",
          "gst_rate",
          "gst_rate_freight",
          "escalation",
          // "specification",
          // "dimension_standard",
        ].join(",");

        const queryParams = new URLSearchParams();
        queryParams.set("fields", fields);
        queryParams.set("page", state.page);
        // queryParams.set("page_size", state.page_size);
        queryParams.set("ordering", state.ordering);
        if (state.search) {
          queryParams.set("search", state.search);
        }
        const apiResponse = await getRequest(
          `material-type/?${queryParams.toString()}`,
          state
        );
        if (apiResponse.data.results.success) {
          setMaterialTypeList(apiResponse.data.results.data);
          settotalCount(apiResponse.data.count);
        } else {
          ErrorNotificationMsg(t("error_in_material_type"));
        }
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    };
    getMaterialTypeList();
  }, [state, newMaterialTypeAdded, reload, t]);

  // function To Handle Table Change
  const handleTableChange = async (pagination, filters, sorter, ordering) => {
    const orderBy = GetSortingEnumValue(sorter.columnKey, sorter.order);
    setState({
      ...state,
      page: pagination.current,
      pagesize: pagination.pageSize,
      ordering: orderBy,
    });
    setSelectedRow(selectedRow);
  };

  // function to shorting enum Value
  const GetSortingEnumValue = (sortField, sortOrder) => {
    let sortingEnumKey = "id";
    if (sortField === "category_tree_name" && sortOrder === "ascend") {
      sortingEnumKey = "category_tree__name";
    }
    if (sortField === "category_tree_name" && sortOrder === "descend") {
      sortingEnumKey = "-category_tree__name";
    }
    if (sortField === "type_name" && sortOrder === "ascend") {
      sortingEnumKey = "type_name";
    }
    if (sortField === "type_name" && sortOrder === "descend") {
      sortingEnumKey = "-type_name";
    }
    if (sortField === "hsn_code" && sortOrder === "ascend") {
      sortingEnumKey = "hsn_code";
    }
    if (sortField === "hsn_code" && sortOrder === "descend") {
      sortingEnumKey = "-hsn_code";
    }

    return sortingEnumKey;
  };

  // Delayed search callback function
  const searchCallbackDelayed = (event) => {
    const { value } = event.target;
    debounceLoadData(value, onSearch);
  };

  // Function to handle search input and update state
  const onSearch = (value) => {
    setState({
      ...state,
      search: value,
    });
  };

  // Row selection configuration for the table
  const rowSelection = {
    onSelect: (record, selected, selectedRows) => {
      const updatedSelectedRowKeys = selected
        ? [...selectedRow, record.id]
        : selectedRow.filter((key) => key !== record.id);
      setSelectedRow(updatedSelectedRowKeys);
    },
    onSelectAll: (selected, selectedRows, changeRows) => {
      const updatedSelectedRowKeys = selected
        ? [...selectedRow, ...changeRows.map((row) => row.id)]
        : selectedRow.filter(
            (key) => !changeRows.find((row) => row.id === key)
          );
      setSelectedRow(updatedSelectedRowKeys);
    },
    selectedRowKeys: selectedRow,
  };

  // Debounce function to delay search requests
  const debounceLoadData = debounce((value, onSearch) => {
    onSearch(value);
  }, 1000);

  // Function to show the modal
  const showModal = () => {
    setIsModalOpen(true);
  };

  // Function to handle modal OK button click
  const handleOk = () => {
    setIsModalOpen(false);
  };

  // Function to handle modal Cancel button click
  const handleCancel = () => {
    setIsModalOpen(false);
  };

  // Function to handle archiving of selected items
  const handleArchive = async (id = "") => {
    let ids = selectedRow;
    if (ids.length === 0) {
      ids.push(id);
    }
    try {
      let apiResponse = await postRequest("material-type-archive/", {
        deleted: ids,
      });
      if (apiResponse.data.success) {
        let totalArchive = totalCount - ids?.length;

        if (totalArchive < 11) {
          setState((prevState) => ({
            ...prevState,
            page: 1,
            search: "",
          }));
          setReload(!reload);
        } else {
          setReload(!reload);
        }
        SuccessNotificationMsg(t("material_type_archive_successfully6"));

        setSelectedRow([]);
      } else {
        setState((prevState) => ({
          ...prevState,
          page: 1,
          search: "",
        }));
        ErrorNotificationMsg(t("error_in_archive"));
      }
    } catch (error) {
      setState((prevState) => ({
        ...prevState,
        page: 1,
        search: "",
      }));
    }
  };

  const [isModalOpenBulk, setIsModalOpenBulk] = useState(false);

  // Function to show the bulk upload modal
  const showBulkUpload = () => {
    setIsModalOpenBulk(true);
  };

  // Function to handle bulk upload modal OK button click
  const handleOkBulk = () => {
    setIsModalOpenBulk(false);
  };

  // Function to handle bulk upload modal Cancel button click
  const handleCancelBulk = () => {
    setIsModalOpenBulk(false);
  };

  const columns = [
    {
      title: "Category",
      dataIndex: "category_tree_name",
      key: "category_tree_name",
      sorter: true,
      width: 180,
    },
        {
      title: "Sub Category",
      dataIndex: "sub_category",
      key: "type_name",
      sorter: true,
      width: 180,
    },
    {
      title: "Material Type",
      dataIndex: "type_name",
      key: "type_name",
      sorter: true,
      width: 180,
      render: (text, record) => (
        <>
          {text}
          {record.category_tree_name === "Structural steel" && (
            <Tooltip
              title={
                <>
                  <div>
                    <b>Specification:</b> {record.specification || "N/A"}
                  </div>
                  <div>
                    <b>Dimension Standard:</b>{" "}
                    {record.dimension_standard || "N/A"}
                  </div>
                </>
              }
            >
              <InfoCircleOutlined style={{ color: "#1890ff", marginLeft: 6 }} />
            </Tooltip>
          )}
        </>
      ),
    },

    {
      title: "HSN Code",
      dataIndex: "hsn_code",
      key: "hsn_code",
      sorter: true,
    },
    {
      title: "GST (%) on Item",
      dataIndex: "gst_rate",
      key: "gst_rate",
      sorter: true,
      render: (value) => <span>{value} %</span>,
    },
    {
      title: "GST (%) on Freight",
      dataIndex: "gst_rate_freight",
      key: "gst_rate",
      sorter: true,
      render: (value) => <span>{value} %</span>,
    },
    {
      title: "Escalation (%)",
      dataIndex: "escalation",
      key: "escalation",
      sorter: true,
      render: (value) => <span> {value} %</span>,
    },
    {
      title: "Action",
      key: "operation",
      width: "100px",
      render: (text, record) => (
        <Space>
          <Tooltip
            color={colors}
            placement="left"
            title={<span style={{ color: "#fff" }}>Edit</span>}
          >
            <Link>
              {authUserData()?.permission.includes(
                "material_type|change_materialtype"
              ) && (
                <Editicon
                  onClick={() => handleUpdate(record.id)}
                  style={{ cursor: "pointer" }}
                />
              )}
            </Link>
          </Tooltip>
          <Tooltip
            color={colors}
            placement="right"
            title={<span style={{ color: "#fff" }}>Archive</span>}
          >
            <Popconfirm
              className="action"
              title={t("pop_title")}
              okText={t("pop_ok")}
              cancelText={t("pop_cancel")}
              onConfirm={() => handleArchive(record.id)}
            >
              {authUserData()?.permission.includes(
                "material_type|delete_materialtype"
              ) && <Archive style={{ cursor: "pointer" }} />}
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];
  const colors = ["#094E8F"];
  const handleUpdate = (id) => {
    setModalKey((prevKey) => !prevKey);
    setMaterialTypeId(id);
    showModal();
  };

  // function to Add the Material Typpe Add
  const handleAdd = () => {
    setMaterialTypeId(null);
    showModal();
    setState({ ...state, ordering: "id" });
    setReload((reload) => !reload);
  };
  return (
    <>
      <div className="ListingTopBar">
        <div className="pagenamewrap">
          <div className="pagename">
            <h3>{t("place_material_type")}</h3>

            <Breadcrumb className="Breadcrumb-Sec">
              <Breadcrumb.Item>
                <Link to="/dashboard">{t("home")}</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>{t("place_material_type")}</Breadcrumb.Item>
            </Breadcrumb>
          </div>
          <Space>
            <div className="searchwrp">
              <Space direction="vertical">
                <Search
                  value={searchText}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSearchText(value);
                    debounceLoadData(value, onSearch);
                  }}
                  allowClear
                  onSearch={(value) => {
                    setSearchText(value);
                    onSearch(value);
                  }}
                  prefix={
                    <img
                      src={SearchIcon}
                      alt="SearchIcon"
                      className="SearchIcon"
                      width={22}
                    />
                  }
                  placeholder={t("pl_search_list")}
                  style={{ width: 250 }}
                />
              </Space>
            </div>
            {authUserData()?.permission.includes(
              "material_type|add_materialtype"
            ) && (
              <Tooltip
                color={colors}
                placement="right"
                title={
                  <span style={{ color: "#fff" }}>
                    Create a new material type for categorizing and managing
                    materials within your project or system.
                  </span>
                }
              >
                <Button
                  className="BlueShadowBtn"
                  type="primary"
                  onClick={handleAdd}
                  icon={<img src={PlusSquareOutlined} alt="Plus Square" />}
                >
                  {t("pl_add_material_type")}
                </Button>
              </Tooltip>
            )}

            <Modal
              className="ModelPopupSecMaterialType"
              title={
                materialTypeId
                  ? t("Edit Material Type")
                  : t("Add Material Type")
              }
              open={isModalOpen}
              onOk={handleOk}
              onCancel={handleCancel}
              closable={false}
              wrapClassName="modal-wrapper"
              keyboard={false}
            >
              <AddMaterialType
                reload={reload}
                setReload={setReload}
                handleCancel={handleCancel}
                materialTypeId={materialTypeId}
                modalKey={modalKey}
                setStateM={setState}
              />
            </Modal>
            <Modal
              className="ModelPopupSec"
              title="Upload File"
              open={isModalOpenBulk}
              onOk={handleOkBulk}
              onCancel={handleCancelBulk}
              closable={false}
              wrapClassName="modal-wrapper"
            >
              <BulkMaterialTypeUpload
                setNewMaterialTypeAdded={setNewMaterialTypeAdded}
                newMaterialTypeAdded={newMaterialTypeAdded}
                materialTypeId={materialTypeId}
                handleCancelBulk={handleCancelBulk}
              />
            </Modal>

            {selectedRow.length > 0 && (
              <Popconfirm
                className="action"
                title={t("pop_title")}
                okText={t("pop_ok")}
                cancelText={t("pop_cancel")}
                placement="left"
                onConfirm={() => handleArchive()}
              >
                {authUserData()?.permission.includes(
                  "material_type|delete_materialtype"
                ) && (
                  <ButtonField
                    className="BlueShadowBtn"
                    type="primary"
                    htmlType="button"
                    label={t("archiveList")}
                  />
                )}
              </Popconfirm>
            )}

            <Tooltip placement="topLeft" title="View Archived Records">
              <Link className="Archiveicon" to="/material-type/archive">
                <Archiveicon />
              </Link>
            </Tooltip>
            <Tooltip placement="topLeft" title="Bulk Upload">
              <Link className="BulkFileUplodIcon" onClick={showBulkUpload}>
                <img src={BulkUploadCsv} alt="" />
              </Link>
            </Tooltip>
          </Space>
        </div>
      </div>
      <section className="grid-sec tablegridsec">
        <div className="PR-body">
         <div className="tablewrp">
          <Table
            rowSelection={rowSelection}
            columns={columns}
            rowKey={(record) => record.id}
            dataSource={materialTypeList}
            loading={loading}
            pagination={{
              current: state.page,
              pageSize: state.pagesize,
              total: totalCount,
              showTotal: (total) => `Total ${total} items`,
              showSizeChanger: true,
              locale: {
                items_per_page: "Items / Page", // Text for the size changer
              },
            }}
            onChange={handleTableChange}
          />
          </div>
        </div>
      </section>
    </>
  );
};
export default MaterialTypeList;
